import type { AddRowTableConfig } from "./AddRowTable";
import {
  ECO_OFFICE_COLUMNS,
  ECO_OFFICE_COLUMN_WIDTH,
  ECO_OFFICE_HEADER_LABELS,
  ECO_OFFICE_TOOLTIPS,
  isEcoOfficeCategory,
} from "./ecoOfficeCatalogue";
import {
  EQUIPMENT_TYPE_FIELD_KEY,
  HEAVY_EQUIPMENT_TOOLTIPS_BY_FIELD_KEY,
  fieldKeyOf,
  isHeavyEquipmentCategory,
  rowValuesByFieldKey,
  visibleFieldKeysFor,
} from "./heavyEquipmentCatalogue";
import {
  WATER_COLUMN_WIDTH,
  WATER_HEADER_LABELS,
  WATER_LABELS,
  WATER_TOOLTIPS_BY_FIELD_KEY,
  isSustainableWaterCategory,
  waterFieldKeyOf,
  waterRowValuesByFieldKey,
  waterVisibleFieldKeysFor,
} from "./sustainableWaterCatalogue";

/**
 * Which assessments render through the shared add-row table, and the copy and
 * geometry each one supplies.
 *
 * `AddRowTable` asks callers to bind their config rather than passing it at
 * every call site. Procurement does that with its own wrapper component, which
 * it needs because its footer counts completed rows. The gold assessments have
 * no such state, so one lookup serves both and Calculator keeps a single
 * add-row branch instead of one per category.
 */

const ECO_OFFICE_TABLE_CONFIG: AddRowTableConfig = {
  widths: ECO_OFFICE_COLUMN_WIDTH,
  headerLabels: ECO_OFFICE_HEADER_LABELS,
  tooltipFor: (column) => ECO_OFFICE_TOOLTIPS[column.label],
  headingColumns: [ECO_OFFICE_COLUMNS.wasteType],
  addLabel: "Add waste type",
  emptyNote: (
    <>
      No waste types yet. Use <strong>Add waste type</strong> below to record
      each kind of office waste your team produces.
    </>
  ),
  // No minimum, unlike Procurement: a lab may remove every row and start over.
  minRows: 0,
};

const HEAVY_EQUIPMENT_TABLE_CONFIG: AddRowTableConfig = {
  // No widths or headerLabels: cards only, so neither is ever read.
  tooltipFor: (column) =>
    HEAVY_EQUIPMENT_TOOLTIPS_BY_FIELD_KEY[fieldKeyOf(column.id)],
  // Empty: cards are headed by the row's name, which says its type already.
  headingColumns: [],
  emptyNote: (
    <>
      No equipment yet. Use <strong>Add equipment</strong> below to record each
      major piece of equipment in your lab.
    </>
  ),
  minRows: 0,
  // No `layout: "cards"` any more. One table per equipment type is 5 to 12
  // columns, which fits the wide table; it was 55 that never did.
  hiddenFor: (column, values) => {
    const rowValues = rowValuesByFieldKey(values);
    const equipmentType = rowValues[EQUIPMENT_TYPE_FIELD_KEY] ?? "";
    return !visibleFieldKeysFor(equipmentType, rowValues).has(
      fieldKeyOf(column.id),
    );
  },
};

const WATER_TABLE_CONFIG: AddRowTableConfig = {
  widths: WATER_COLUMN_WIDTH,
  headerLabels: WATER_HEADER_LABELS,
  tooltipFor: (column) =>
    WATER_TOOLTIPS_BY_FIELD_KEY[waterFieldKeyOf(column.id)],
  headingColumns: [WATER_LABELS.waterCategory, WATER_LABELS.primaryUse],
  addLabel: "Add water use",
  emptyNote: (
    <>
      No water use recorded yet. Use <strong>Add water use</strong> below for
      each grade of water your lab uses.
    </>
  ),
  minRows: 0,
  // Five columns, so the shared table still fits at xl. Only the free-text
  // column comes and goes, which a table can absorb.
  hiddenFor: (column, values) =>
    !waterVisibleFieldKeysFor(waterRowValuesByFieldKey(values)).has(
      waterFieldKeyOf(column.id),
    ),
};

/**
 * The add-row config for a category, or null when it renders some other way.
 *
 * Eco Office takes the level because only gold uses this table; bronze keeps
 * its own hardcoded page.
 */
export const addRowConfigFor = (
  category: string | null,
  level: string,
): AddRowTableConfig | null => {
  if (isEcoOfficeCategory(category) && level === "gold") {
    return ECO_OFFICE_TABLE_CONFIG;
  }
  if (isHeavyEquipmentCategory(category)) {
    return HEAVY_EQUIPMENT_TABLE_CONFIG;
  }
  if (isSustainableWaterCategory(category)) {
    return WATER_TABLE_CONFIG;
  }
  return null;
};
